// ============================================================================
// BOQ — library classification maps
// ============================================================================
// Turns raw item_version_classifications rows into the per-item maps the
// library UI works in: trade group, canonical chapter, canonical sub-trade.
//
// Shared by the live library (useBoqLibrary) and the archive viewer
// (useBoqLibraryVersions) so an archive is classified by exactly the same
// rules as the library it was taken from. The classification TAXONOMY is not
// archived — only the tags — so an archive resolves against today's nodes.
// ============================================================================

import { CHAPTER_BY_TRADE_GROUP } from './tradeChapterCrosswalk';

// PostgREST caps a single response at 1000 rows. The library catalogue exceeds
// that (item_versions ≈1.3k, classifications ≈2.4k), so a plain select silently
// truncates — which leaves the trade_group map incomplete and stops lines from
// rolling up to their canonical chapter. Fetch in pages to get the full set.
// `makeQuery` must return a fresh query builder on each call.
export async function fetchAllRows(makeQuery, pageSize = 1000) {
  const all = [];
  for (let from = 0; ; from += pageSize) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await makeQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < pageSize) break;
  }
  return all;
}

/**
 * Build the per-item classification maps.
 *
 * @param versionRows        [{ id, item_code }]                        — item versions in scope
 * @param classificationRows [{ item_version_id, classification_node_id }]
 * @param taxonomyRows       [{ id, code }]                             — classification_taxonomies
 * @param nodeRows           [{ id, taxonomy_id, code, label, sort_order, parent_id, maps_to_node_id, short_code, series_start, series_end }]
 */
export function buildClassificationMaps({ versionRows, classificationRows, taxonomyRows, nodeRows }) {
  const taxonomies = taxonomyRows || [];
  const nodes = nodeRows || [];

  // trade_group nodes, in display order.
  const tradeTax = taxonomies.find((t) => t.code === 'trade_group');
  const tradeGroups = nodes
    .filter((n) => tradeTax && n.taxonomy_id === tradeTax.id)
    .map((n) => ({
      id: n.id, code: n.code, label: n.label, sortOrder: n.sort_order,
      mapsToNodeId: n.maps_to_node_id || null,
      // The TRADE segment of a generated item code.
      shortCode: n.short_code || null,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const tgNodeById = new Map(tradeGroups.map((n) => [n.id, n]));

  // brio_trade nodes: chapters (roots) + sub-trades (children).
  const brioTax = taxonomies.find((t) => t.code === 'brio_trade');
  const brioById = new Map();
  nodes.filter((n) => brioTax && n.taxonomy_id === brioTax.id).forEach((n) => brioById.set(n.id, n));

  // Chapter a trade_group rolls up to. Source of truth is the DB link
  // (classification_nodes.maps_to_node_id, set by the crosswalk migration);
  // the hard-coded map is only a defensive fallback if the link is missing.
  const chapterForTradeGroup = (tg) => {
    if (tg.mapsToNodeId && brioById.has(tg.mapsToNodeId)) return brioById.get(tg.mapsToNodeId).label;
    return CHAPTER_BY_TRADE_GROUP[tg.code] || null;
  };

  // brio_trade sub-trades grouped under their chapter — for the create /
  // classify pickers. Ordered by chapter then sub-trade sort.
  const brioSubtrades = [...brioById.values()]
    .filter((n) => n.parent_id)
    .map((n) => ({
      id: n.id, code: n.code, label: n.label,
      // The SUBTRADE segment of a generated item code.
      shortCode: n.short_code || null,
      // The sub-trade's own range. Split across its trade types when it has
      // any; the item number's source when it has none.
      seriesStart: n.series_start,
      seriesEnd: n.series_end,
      chapterId: n.parent_id,
      chapterLabel: brioById.get(n.parent_id)?.label || '',
      chapterSort: brioById.get(n.parent_id)?.sort_order ?? 0,
      sort: n.sort_order ?? 0,
    }))
    .sort((a, b) => (a.chapterSort - b.chapterSort) || (a.sort - b.sort) || a.label.localeCompare(b.label));

  // trade_type: a flat taxonomy of its own, each entry anchored to the
  // brio_trade sub-trade it sits under via maps_to_node_id. Ordered so the
  // picker lists them the way the Taxonomies page does.
  const typeTax = taxonomies.find((t) => t.code === 'trade_type');
  const typeById = new Map();
  nodes.filter((n) => typeTax && n.taxonomy_id === typeTax.id).forEach((n) => typeById.set(n.id, n));
  const tradeTypes = [...typeById.values()]
    .map((n) => ({
      id: n.id,
      code: n.code,
      label: n.label,
      shortCode: n.short_code || null,
      anchorId: n.maps_to_node_id || null,
      // The band of sequence numbers this type's items draw from.
      seriesStart: n.series_start,
      seriesEnd: n.series_end,
      sort: n.sort_order ?? 0,
    }))
    .sort((a, b) => (a.sort - b.sort) || a.label.localeCompare(b.label));

  const versionItemCode = new Map((versionRows || []).map((v) => [v.id, v.item_code]));
  const tradeGroupByItem = new Map();
  const chapterByItem = new Map();
  const subtradeByItem = new Map();
  const brioSubtradeNodeByItem = new Map();
  const tradeTypeNodeByItem = new Map();   // itemCode → trade_type node id
  const unmappedTradeGroups = new Set();   // gap-guard surface (dev warning)

  (classificationRows || []).forEach((c) => {
    const code = versionItemCode.get(c.item_version_id);
    if (!code) return;
    // trade_group node → keep for the rolled-up chapter (and as a fallback
    // sub-trade label when no brio sub-trade tag exists).
    const tg = tgNodeById.get(c.classification_node_id);
    if (tg) {
      if (!tradeGroupByItem.has(code)) tradeGroupByItem.set(code, tg);
      const chap = chapterForTradeGroup(tg);
      // Don't override a chapter already set from an (authoritative) brio_trade tag.
      if (chap) { if (!chapterByItem.has(code)) chapterByItem.set(code, chap); }
      else if (tg.code !== 'MISC' && tg.code !== 'BASIC') unmappedTradeGroups.add(tg.code);
      return;
    }
    // brio_trade node → authoritative chapter (overrides any trade_group one);
    // a sub-trade node (has a parent) also gives the canonical sub-trade label.
    const bn = brioById.get(c.classification_node_id);
    if (bn) {
      const chap = bn.parent_id ? (brioById.get(bn.parent_id)?.label || bn.label) : bn.label;
      if (chap) chapterByItem.set(code, chap);
      if (bn.parent_id) { subtradeByItem.set(code, bn.label); brioSubtradeNodeByItem.set(code, bn.id); }
      return;
    }
    // trade_type tag. Descriptive today: it records which trade type the item
    // belongs to, and nothing derives an item code from it yet.
    if (typeById.has(c.classification_node_id)) {
      tradeTypeNodeByItem.set(code, c.classification_node_id);
    }
  });

  return {
    tradeGroups, tradeGroupByItem, chapterByItem, subtradeByItem,
    brioSubtrades, brioSubtradeNodeByItem,
    tradeTypes, tradeTypeNodeByItem,
    unmappedTradeGroups,
  };
}
